<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">
        {{ item?.id ? $t('stockItemEditModalTitle') : $t('stockItemCreateModalTitle') }}
      </div>
    </template>
    <Spinner v-if="dataIsSending"/>
    <div v-else class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="warehouseItem_category_modal"
                          v-model="selectedCategory"
                          data-key="id"
                          @change="changeCategory"
                          :clearSearchData="!visible"
                          :options="warehouseCategories"
                          :loading="!warehouseCategories"
                          :filterPlaceholder="$t('findCategoryPlaceholder')"
                          optionLabel="name"
                          :filter="true"
                          :filterFields="['name']"
                          :showClear="true">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <span>{{ slotProps.value.name }}</span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>
            <template #option="slotProps">
              <div>
                <span>{{ slotProps.option.name }}</span>
              </div>
            </template>
          </CustomDropdown>
          <label for="warehouseItem_category_modal">{{ $t('Category') }}</label>
        </div>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputText id="warehouse_item_code_modal" :class="{'p-invalid' : submitted && !itemData.code}" @input="changeCode" v-model.trim="itemData.code" autocomplete="off"/>
          <label for="warehouse_item_code_modal">{{ $t('Code') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !itemData.code">{{ $t('Required field') }}</small>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputText id="warehouse_item_name_modal" :class="{'p-invalid' : submitted && !itemData.name}" v-model.trim="itemData.name" autocomplete="off"/>
          <label for="warehouse_item_name_modal">{{ $t('Name') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !itemData.name">{{ $t('Required field') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputNumber id="warehouseItem_qty_modal"
                       :locale="computedNumberInputLocale"
                       showButtons
                       v-model="itemData.qty"
                       mode="decimal"
                       :min="0"
                       :max="999999"
                       :disabled="!$store.getters.isAccountantOrHigher"
                       :minFractionDigits="is_decimal ? 2 : null"
                       :maxFractionDigits="is_decimal ? 2 : null"
                       autocomplete="off"/>
          <label for="warehouseItem_qty_modal">{{ $t('Qty') }}</label>
        </div>

        <div class="p-d-flex p-ai-center p-mt-1">
          <Checkbox id="allow-decimal-qty" :binary="true" v-model="is_decimal"/>
          <div>
            <label for="allow-decimal-qty">
              <div class="pointer p-p-1">{{ $t('allowDecimalQtyLabel') }}</div>
            </label>
          </div>
        </div>
      </div>
      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="warehouseItem_unit_modal"
                          :class="{'p-invalid' : submitted && !selectedUnit}"
                          v-model="selectedUnit"
                          :preventFilter="true"
                          :options="units">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <span>{{ unitOptionLabel(slotProps.value) }}</span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>
            <template #option="slotProps">
              <div>
                <span>{{ unitOptionLabel(slotProps.option) }}</span>
              </div>
            </template>
          </CustomDropdown>
          <label for="warehouseItem_unit_modal">{{ $t('unitLabel') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !selectedUnit">{{ $t('Required field') }}</small>
      </div>
      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
<!--          :inputClass="{'p-invalid' : itemData.sell_price && +itemData.sell_price && itemData.sell_price < itemData.purchase_price}"-->
          <InputNumber id="warehouseItem_purchase_price_modal"
                       :locale="computedNumberInputLocale"
                       @input="updatePurchasePrice($event.value)"
                       v-model="itemData.purchase_price"
                       :inputClass="{ 'p-invalid': sellingPriceIsTooLow() }"
                       mode="decimal"
                       :min="0"
                       :minFractionDigits="2"
                       :maxFractionDigits="4"
                       autocomplete="off"/>
          <label for="warehouseItem_purchase_price_modal">{{ $t('Purchase price') }}</label>
        </div>
      </div>
      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
<!--          :inputClass="{'p-invalid' : (submitted && !itemData.sell_price) || (itemData.sell_price && +itemData.sell_price && itemData.sell_price < itemData.purchase_price)}"-->
          <InputNumber id="warehouseItem_sell_price_modal"
                       :locale="computedNumberInputLocale"
                       @input="updateSellPrice($event.value)"
                       :inputClass="{ 'p-invalid': (submitted && !itemData.sell_price) || sellingPriceIsTooLow() }"
                       v-model="itemData.sell_price"
                       mode="decimal"
                       :min="0"
                       :minFractionDigits="2"
                       :maxFractionDigits="4"
                       autocomplete="off"/>
          <label for="warehouseItem_sell_price_modal">{{ $t('Selling price') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !itemData.sell_price">{{ $t('Required field') }}</small>
      </div>

      <div class="p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5" style="margin-top: -22px">
        <div class="p-text-secondary p-d-flex p-ai-center">
          <i class="ti-info-alt p-mr-1"/>
          <small>{{ $t('pricesExcludingVatLabel') }}</small>
        </div>
      </div>

<!--      DON'T DELETE! min and max quantity inputs-->
<!--      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">-->
<!--        <div class="p-float-label">-->
<!--          <InputNumber id="warehouseItem_min_qty_modal" :locale="computedNumberInputLocale" v-model="itemData.min_qty" mode="decimal" :min="0" :minFractionDigits="is_decimal ? 2 : null" :maxFractionDigits="is_decimal ? 2 : null" autocomplete="off"/>-->
<!--          <label for="warehouseItem_min_qty_modal">Minimum stock qty</label>-->
<!--        </div>-->
<!--      </div>-->

<!--      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">-->
<!--        <div class="p-float-label">-->
<!--          <InputNumber id="warehouseItem_max_qty_modal" :locale="computedNumberInputLocale" v-model="itemData.max_qty" mode="decimal" :min="0" :minFractionDigits="is_decimal ? 2 : null" :maxFractionDigits="is_decimal ? 2 : null" autocomplete="off"/>-->
<!--          <label for="warehouseItem_max_qty_modal">Maximum stock qty</label>-->
<!--        </div>-->
<!--      </div>-->

      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputNumber id="warehouse_item_margin_modal"
                       :locale="computedNumberInputLocale"
                       @input="markupOnInput($event.value)"
                       v-model="itemData.margin"
                       class="table-body__margin-input"
                       mode="decimal"
                       :min="0"
                       :max="1000000"
                       :minFractionDigits="0"
                       :maxFractionDigits="2"
                       autocomplete="off"/>
          <label for="warehouse_item_margin_modal">{{ $t('markupPercentLabel') }} (%)</label>
        </div>
      </div>

      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">
        <div class="p-float-label">
          <InputNumber id="warehouse-item-min-stock-modal"
                       :locale="computedNumberInputLocale"
                       showButtons
                       v-model="itemData.min_qty"
                       mode="decimal"
                       :min="0"
                       :max="999999"
                       :disabled="!$store.getters.isAccountantOrHigher"
                       :minFractionDigits="is_decimal ? 2 : 0"
                       :maxFractionDigits="is_decimal ? 2 : 0"
                       autocomplete="off"/>
          <label for="warehouse-item-min-stock-modal">{{ $t('minStockQtyLabel') }}</label>
        </div>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
        <LimitedCharsTextarea :submitted="submitted"
                              v-model="itemData.comment"
                              :maxChars="commentMaxChars"
                              :id="'warehouse-item-comment'"
                              :label="$t('Notes')"/>
      </div>
    </div>

    <template #footer>
      <Button :label="$t('Cancel')" icon="pi pi-times" class="p-button-text" @click="close"/>
      <Button :label="$t('Save')"
              :disabled="dataIsSending || disableSaveButton"
              :loading="dataIsSending"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>
  </Modal>

  <ConfirmModal :visible="confirmSellingPriceIsTooLowModal"
                :text="$t('sellingPriceLowerThanPurchaseAlert') + ' ' + $t('confirm.proceed')"
                @close="closeConfirmSellingPriceIsTooLowModal"
                @confirm="saveItemAnyway"/>
  <ItemExistsModal :visible="itemExistsModal"
                   :itemId="itemData?.id ? +itemData.id : null"
                   type="warehouseItem"
                   :items="foundItems"
                   @select-existing="selectExistingItem"
                   @close="closeItemExistsModal"/>
</template>

<script>
import httpClient from "@/services/http.services";
import generateMixins from "@/mixins/generateMixins";
import httpMixins from "@/mixins/httpMixins";
import constants from "@/constants";
import formatMixins from "@/mixins/formatMixins";
import units from "@/translations/units";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import { computeByContext, toNum } from '@/utils/pricing';
import ItemExistsModal from "@/components/ItemExistsModal.vue";
import foundModalItemsMixin from "@/mixins/foundModalItemsMixin";

export default {
  mixins: [
    generateMixins,
    httpMixins,
    formatMixins,
    showErrorsMixins,
    foundModalItemsMixin
  ],
  components: { ItemExistsModal },
  emits: ['close', 'update-items', 'update-item'],
  name: 'WarehouseItemModal',
  props: {
    // disableQtyEditing: {
    //   type: Boolean,
    //   default: false,
    // },
    item: Object,
    // categories: Array,
    visible: Boolean,
    selectedCategoryId: {
      type: [Number, null],
      default: null
    },
    modalLayer: {
      type: Number,
      default: 1
    },
  },
  data() {
    return {
      createNewItemAnyway: false,
      ignoreSellingPriceIsTooLow: false,
      confirmSellingPriceIsTooLowModal: false,
      commentMaxChars: 500,
      dataIsSending: false,
      disableSaveButton: false,
      is_decimal: false,
      selectedUnit: null,
      selectedCategory: null,
      isRecurring: false,
      useConsumable: false,
      itemData: {},
      submitted: false,
      constants,
      settings,
      warehouseCategories: null,
      units,
    }
  },
  watch: {
    item(value) {
      this.createItemData(value)
    },
    '$store.state.secondLayerIsOpened'(value) {
      if (this.modalLayer !== 1) return
      if (!value) {
        if (this.confirmSellingPriceIsTooLowModal) {
          this.closeConfirmSellingPriceIsTooLowModal()
        }
      }
    },
    '$store.state.thirdLayerIsOpened'(value) {
      if (this.modalLayer !== 2) return
      if (!value) {
        if (this.confirmSellingPriceIsTooLowModal) {
          this.closeConfirmSellingPriceIsTooLowModal()
        }
      }
    },
    visible() {
      if (!this.visible) {
        this.createNewItemAnyway = false
        this.ignoreSellingPriceIsTooLow = false
        this.disableSaveButton = false
        this.dataIsSending = false
        this.submitted = false
        this.selectedCategory = null
        this.selectedUnit = null
        this.is_decimal = false
        // this.is_decimal = false
        this.warehouseCategories = null
      } else {
        this.getWarehouseCategories().then(() => this.selectCategory())
      }
    },
  },
  methods: {
    // createItemData(value) {
    //   if (!value) return
    //   this.itemData = {
    //     ...value,
    //     qty: value.qty ? +value.qty : 0,
    //     purchase_price: value.purchase_price ? +value.purchase_price : null,
    //     sell_price: value.sell_price ? +value.sell_price : null,
    //     min_qty: value.min_qty ? +value.min_qty : null,
    //     max_qty: value.max_qty ? +value.max_qty : null,
    //   }
    //
    //   this.is_decimal = !!value.is_decimal;
    //
    //   if (value.unit) {
    //     this.selectedUnit = this.units.find(unit => unit.num === value.unit)
    //   } else {
    //     this.selectedUnit = this.units.find(unit => unit.num === 1)
    //   }
    //   this.calculateItemMargin()
    // },
    createItemData(value = null) {
      const data = value || {}

      this.itemData = {
        ...data,
        qty: Number(data.qty || 0),
        purchase_price: data.purchase_price ? Number(data.purchase_price) : null,
        sell_price: data.sell_price ? Number(data.sell_price) : null,
        min_qty: data.min_qty ? Number(data.min_qty) : null,
        max_qty: data.max_qty ? Number(data.max_qty) : null,
      }

      this.is_decimal = !!data.is_decimal

      this.selectedUnit = data.unit
          ? this.units.find(unit => Number(unit.num) === Number(data.unit))
          : this.units.find(unit => Number(unit.num) === 1)

      this.calculateItemMargin()
    },
    unitOptionLabel(unit) {
      return unit?.label?.[this.$i18n.locale] || unit?.label?.en || ''
    },
    openConfirmSellingPriceIsTooLowModal() {
      this.confirmSellingPriceIsTooLowModal = true
      if (this.modalLayer === 2) {
        this.$store.commit('toggleThirdLayer', true)
      } else if (this.modalLayer === 1) {
        this.$store.commit('toggleSecondLayer', true)
      }
    },
    closeConfirmSellingPriceIsTooLowModal() {
      this.confirmSellingPriceIsTooLowModal = false
      if (this.modalLayer === 2) {
        this.$store.commit('toggleThirdLayer', false)
      } else if (this.modalLayer === 1) {
        this.$store.commit('toggleSecondLayer', false)
      }
    },
    calculateItemMargin() {
      // console.log('calculateItemMargin')
      if (!this.itemData) return
      const item = this.itemData
      const margin = ((item.sell_price * 100) / item.purchase_price) - 100
      if (+margin && isFinite(+margin)) {
        item.margin = this.formatDecimal(margin, false, 2)
      } else {
        item.margin = null
      }
    },
    updateMargin() {
      if (!this.itemData) return
      if ((this.itemData.purchase_price && +this.itemData.purchase_price) && (this.itemData.sell_price && +this.itemData.sell_price)) {
        this.calculateItemMargin()
      } else {
        this.itemData.margin = null
      }
    },
    markupOnInput(value) {
      if (!this.itemData) return

      if (value < 0) {
        value = 0
      } else if (value === '-') {
        value = null
      }

      if (this.itemData.purchase_price && +this.itemData.purchase_price !== 0) {
        this.itemData.sell_price = this.itemData.purchase_price + (this.itemData.purchase_price * (value / 100))
      } else {
        this.itemData.sell_price = null
      }
    },
    selectCategory() {
      // if (!this.itemData.warehouse_category_id) return
      const categoryId = this.itemData.warehouse_category_id ? +this.itemData.warehouse_category_id : this.selectedCategoryId ? this.selectedCategoryId : null
      if (!categoryId) return

      const selectedCategory = this.warehouseCategories?.find(category => category.id === categoryId)
      if (selectedCategory) {
        this.selectedCategory = selectedCategory
      }
    },
    async getWarehouseCategories() {
      try {
        const { data, status } = await httpClient('warehouse/get-dropdown-categories?sort=sorting')
        if (status === 200 && data?.categories) {
          // this.warehouseCategories = Array.isArray(data) ? data : []
          this.warehouseCategories = data.categories
        }
      } catch(err) {
        this.showError(err)
      }
    },
    changeCategory() { //Don't delete
      this.itemData.categoryWasChanged = this.itemData.warehouse_category_id !== this.selectedCategory?.id
    },
    changeCode(event) {
      this.itemData.raw_code = this.generateRawCode(event.target.value)
    },
    updatePurchasePrice(value) {
      if (!this.itemData) return;

      // нормализация входа
      if (value === '-') {
        value = null;
      } else {
        value = toNum(value);
        if (value !== null) {
          if (value < 0) value = 0;
          if (value > 1_000_000) value = 1_000_000;
        }
      }

      this.itemData.purchase_price = value;

      const bd = this.$store.getters.locationData
      this.itemData.sell_price = computeByContext(
          value,
          bd,
          'warehouse',
          this.$store.getters.canUse('advanced_markup')
      )

      this.updateMargin();
    },
    updateSellPrice(value) {
      if (!this.itemData) return
      if (value < 0) {
        value = 0
      } else if (value > 1000000) {
        value = 1000000
      } else if (value === '-') {
        value = null
      }
      this.itemData.sell_price = value
      // if ()
      this.updateMargin()
    },
    saveItemAnyway() {
      this.ignoreSellingPriceIsTooLow = true
      this.closeConfirmSellingPriceIsTooLowModal()
      this.saveItem()
    },
    async saveItem() {
      this.submitted = true
      if (!this.itemData.name ||
          !this.itemData.code ||
          // !this.itemData.purchase_price ||
          !this.itemData.sell_price ||
          !this.selectedUnit ||
          this.itemData.comment && this.itemData.comment.length > this.commentMaxChars) {
        return false
      }

      if (this.sellingPriceIsTooLow() && !this.ignoreSellingPriceIsTooLow) {
        this.openConfirmSellingPriceIsTooLowModal()
        return false
      }

      this.disableSaveButton = true
      this.dataIsSending = true

      const qty = Number(this.itemData.qty || 0)
      const minQty = Number(this.itemData.min_qty || 0)

      const normalizedQty = this.is_decimal
          ? this.formatDecimal(qty, false, 4)
          : Math.round(qty)

      const normalizedMinQty = minQty > 0
          ? (this.is_decimal ? this.formatDecimal(minQty, false, 4) : Math.round(minQty))
          : null

      const warehouseItem = {
        id: this.itemData.id ?? null,
        warehouse_category_id: this.selectedCategory ? this.selectedCategory.id : null,
        code: this.itemData.code,
        raw_code: this.itemData.raw_code,
        name: this.itemData.name,
        // qty: this.itemData.qty,
        // qty: this.is_decimal && this.itemData.qty ? this.formatDecimal(this.itemData.qty, false) : this.formatDecimal(this.itemData.qty, false, 0),
        qty: normalizedQty,
        min_qty: normalizedMinQty,
        is_decimal: this.is_decimal ? 1 : 0,
        purchase_price: this.itemData.purchase_price,
        sell_price: this.itemData.sell_price,
        unit: this.selectedUnit ? this.selectedUnit.num : null,
        // min_qty: this.itemData.min_qty,
        // max_qty: this.itemData.max_qty,
        // is_decimal: this.is_decimal ? 1 : 0,
        comment: this.itemData.comment,
        categoryWasChanged: this.itemData.categoryWasChanged,
        isNew: false,
        createNewAnyway: this.createNewItemAnyway,
      }

      if (this.itemData.id) {
        try {
          const { status, data } = await httpClient.post(`warehouse/update-item`, warehouseItem)
          // console.log(data)
          if (status === 200 && data?.success) {
            this.$emit('update-items', this.itemData.id)
            this.$emit('update-item', warehouseItem)
            this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: this.settings.toastLife});
            this.close()
          } else if (data?.found_items) {
            this.foundItems = data.found_items
            this.openItemExistsModal()
          } else if (data.error) {
            this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: this.settings.toastLife})
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.dataIsSending = false
          this.disableSaveButton = false
          this.ignoreSellingPriceIsTooLow = false
        }
      } else {
        try {
          const { status, data } = await httpClient.post(`warehouse/create-item`, warehouseItem)
          // console.log(data)
          if (status === 201 && data?.success) {
            this.itemData.id = data.warehouse_item_id
            warehouseItem.id = data.warehouse_item_id
            warehouseItem.isNew = true
            // this.itemData.isNew = true
            // this.$emit('update-items', this.itemData.id)
            // this.$emit('update-item', this.itemData)
            this.$emit('update-items', this.itemData.id)
            this.$emit('update-item', warehouseItem)
            this.$toast.add({severity:'success', detail: this.$t('Data saved'), life: this.settings.toastLife});
            this.close()
          } else if (data?.found_items) {
            this.foundItems = data.found_items
            this.openItemExistsModal()
          } else if (data?.error) {
            this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: this.settings.toastLife});
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.dataIsSending = false
          this.disableSaveButton = false
          this.ignoreSellingPriceIsTooLow = false
        }
      }
    },
    sellingPriceIsTooLow() {
      const sellPrice = Number(this.itemData?.sell_price || 0)
      const purchasePrice = Number(this.itemData?.purchase_price || 0)

      return sellPrice > 0 && purchasePrice > 0 && sellPrice < purchasePrice
    },
    close() {
      this.$emit('close')
      this.submitted = false
    },
  },
  computed: {
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {position: 'absolute', top: this.modalLayer === 2 ? '15px' : 0, width: '95%', maxWidth: '440px', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: this.modalLayer === 2 ? '15px' : 0, maxWidth: '440px', marginTop: '15px'}
      } else {
        return {position: 'absolute', top: this.modalLayer === 2 ? '15px' : 0, maxWidth: '440px'}
      }
    },
  },

}
</script>